import React, { useEffect, useState } from 'react';
import {
  Image,
  ImageSourcePropType,
  Pressable,
  StyleSheet,
  View,
} from 'react-native';
import Button, { ButtonProps } from 'shared/components/vxl/Button/Button';
import IconButton from 'shared/components/vxl/icon/IconButton/IconButton';
import IllustrationMap from 'shared/components/vxl/Illustration/IllustrationMap';
import ExpressiveXXSText from 'shared/components/vxl/typography/expressive/ExpressiveXXSText';
import BodySmText from 'shared/components/vxl/typography/primary/body/BodySmText';
import theme from 'shared/styles/theme';

import { CardType } from './BrazeCard.constants';
import { getCardTypeStyle } from './BrazeCardBackground.helpers';

/**
 * Props for the BrazeCard component.
 * @typedef {Object} BrazeCardProps
 * @property {CardType} type - The type of the card.
 * @property {string} title - The title of the card.
 * @property {string} description - The description of the card.
 * @property {ButtonProps} buttonProps - Props to pass to the Button component.
 */
export interface BrazeCardProps {
  cardType: CardType;
  headlineText: string;
  bodyText: string;
  button: ButtonProps;
  onDismiss?: () => unknown;
  onAppear?: () => void;
  imageSource?: ImageSourcePropType;
}
export type IllustrationKeyType = keyof typeof IllustrationMap;

const BrazeCard = ({
  cardType = CardType.Blush,
  bodyText,
  button,
  headlineText,
  onDismiss,
  onAppear,
  imageSource,
}: BrazeCardProps) => {
  const [isFocused, setIsFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const adjustedRightTextMargin = onDismiss && !imageSource ? 24 : 0;

  useEffect(() => {
    if (onAppear) {
      onAppear();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Pressable
      onHoverIn={() => setIsHovered(true)}
      onHoverOut={() => setIsHovered(false)}
      onBlur={() => setIsFocused(false)}
      onFocus={() => setIsFocused(true)}
      style={({ pressed }) => [
        cardStyles.outerContainer,
        getCardTypeStyle(cardType, pressed, isHovered, isFocused),
      ]}
    >
      {onDismiss ? (
        <View style={cardStyles.crossButtonStyle}>
          <IconButton
            iconKey="CLOSE_CIRCLE"
            iconOnPress={onDismiss}
            iconButtonAccessibilityLabel="Dismiss Braze card"
            testID="braze-card-close-button"
          />
        </View>
      ) : null}
      {imageSource ? (
        <View style={cardStyles.illustration}>
          <Image source={imageSource} width={60} height={60} />
        </View>
      ) : null}
      <ExpressiveXXSText
        style={[
          cardStyles.headingStyle,
          { marginRight: adjustedRightTextMargin },
        ]}
        numberOfLines={2}
        ellipsizeMode="tail"
      >
        {headlineText}
      </ExpressiveXXSText>
      <BodySmText
        style={cardStyles.textStyle}
        numberOfLines={3}
        ellipsizeMode="tail"
      >
        {bodyText}
      </BodySmText>
      <View style={cardStyles.buttonStyle}>
        <Button {...button} size="SMALL" type="SECONDARY" width="HUG" />
      </View>
    </Pressable>
  );
};

const cardStyles = StyleSheet.create({
  buttonStyle: {
    alignSelf: 'flex-start',
  },
  crossButtonStyle: {
    position: 'absolute',
    right: theme.spacing.m,
    top: theme.spacing.m,
    zIndex: 10,
  },
  headingStyle: {
    color: theme.color.expressive.eggplant,
  },
  illustration: {
    height: 60,
    marginBottom: theme.spacing.s,
    width: 60,
  },

  outerContainer: {
    borderColor: theme.color.border.primary,
    borderRadius: 4,
    borderWidth: 1,
    padding: theme.spacing.m,
    position: 'relative',
    width: '100%',
  },
  textStyle: {
    marginVertical: theme.spacing.s,
  },
});

export default BrazeCard;
